import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TimeTravel.css';

export default function TimeTravel() {
  const navigate = useNavigate();
  const publishDate = 'April 29, 2025';

  return (
    <div className="timetravel-page">
      <article className="blogpost-container">
        <button className="back-btn" onClick={() => navigate('/blogs')}>
          ←
        </button>

        <header>
          <h1 className="blog-title">Time Travel for Rookies</h1>
          <time className="blog-date" dateTime="2025-04-29">
            {publishDate}
          </time>
        </header>

      <section className="blog-section">
        <p>
          Impossible, yet possible
        </p>
      </section>

      <section className="blog-section">
        <p>
          The current state of the world puts human life at risk, leaving us with two choices for survival: protection or fleeing.
          While the idea of protection of our species through sustainability is talked about quite often, the concept of fleeing Earth through time travel or wormholes isn’t discussed nearly enough.
          A big reason for that is because we have very minimalistic ideas about the concept of time travel.
          To us humans even the concept of smartphones seemed unrealistic a hundred years ago.
          This is similar to how we think about time travel today.
          Through new technology we are discovering more and more concepts which were supposedly shockingly impossible.
        </p>
      </section>

      <section className="blog-section">
        <p>
          We are always living in the past.
          Light takes 8 minutes and 20 seconds to reach the Earth from the Sun, which means that the Sun we are seeing is in the past.
          Similarly, for most constellations, light takes about 5000 years to reach Earth and for galaxies it’s a couple of billion years and therefore currently they might be even dead in real time.
          And as for the big bang (the creation of the universe) it just begun for us, since we are looking into the past.
          Now coming to the question of how realistic it is, the odds aren’t in our favor due to the lack of advanced technology.
          But given a couple of hundred years, we can see this as a potential reliability for our survival.
        </p>
      </section>

      <section className="blog-section">
        <p>
          Travelling through time can be done in two ways, traveling into the past or traveling into the future.
          One way a person could travel into the future is through wormholes.
          Wormholes are hypothetical tunnel-like structures that could connect two distant points in space-time, allowing faster-than-light travel and time travel.
          In the movie ‘Interstellar’ by Christopher Nolan, one of the characters explains wormholes by taking a piece of paper which is assumed as space and bends it in half without creasing the paper.
          He pokes a pencil through the paper such that both the sides connect through the pencil and that is the wormhole.
          On entering it, one could lead to a whole other side of the universe in an instant.
          After entering it, time works differently from the other region travelled through the wormhole with respect to earth.
          In the movie, the characters get close to a black hole on the other side of the universe and one hour over there would equal to seven years on earth.
          The question this statement arises is why does time slow down near the black hole compared to Earth’s time?
          This is because time is considered as a fourth dimension.
          An object has coordinates of length, width, and height.
          Though, in reality it needs time as its fourth dimension.
          As Jim Al-Khalili states, a movie is made of frames which consists of a certain length and height.
          But it would not exist if it were not played, proving that duration is needed as a fourth dimension for an object to exist.
          We could change the first three dimensions at free will, but we are always prisoners of the fourth one which is time.
          But celestial objects such as black holes or wormholes could manipulate the fourth dimension and therefore the way space is stretched near it could also stretch its time surrounding it making it slower.
          If we were to enter a wormhole that opens at the nearest galaxy, we would be 5000 years into the future for Earth because light takes 5000 years to reach earth which we’d be cutting short by entering the wormhole.
          So, for us it would be a second since that’s what it takes through the journey through the wormhole.
          Another way to travel into the future is by traveling at light speed.
          Albert Einstein says that the faster one moves, the slower they get with respect to the surroundings.
          It can be pictured as if a person entering a spaceship on Earth traveled at light speed, he would be years into the Earth’s future when he’d return.
        </p>

        <figure className="blog-figure">
          <img
            src="/assets/wormholes.png"
            alt="Wormhole connecting a distant black hole 5000 light years away"
            className="blog-image"
          />
          <figcaption className="figcaption">
            Figure 1: Wormhole connecting a distant black hole 5000 light years away
          </figcaption>
        </figure>
      </section>

      <section className="blog-section">
        <p>
          Travelling into the past is considered to be challenging.
          Well, how is that done?
          The idea is that a black hole’s gravitational pull is so strong that even light can’t escape it and time slows down near it compared to earthly time.
          A black hole rotating at high speeds can create a wormhole and they are supposedly not prisoners of the fourth dimension, which is time.
          We know that entering a wormhole leads us to different positions in space, but what if it can lead us to different points in time?
          At the time being, we couldn’t control what time it would send us to, but if we are somehow able to find the exact trajectory of entering the wormhole such that it can lead us to the desired time, we can possibly travel into the past.
        </p>
      </section>

      <section className="blog-section">
        <p>
          The issue with traveling into the past is paradoxes that can disturb the normal flow state of time.
          An object travelling has its own defined path called the world-line.
          For example, a world-line for a person is the path of their entire journey which is the moment from their birth to death.
          Their dimensions would be for example, 6 feet in height, 2 feet thick, 2 feet wide and 87 years in duration.
          This world-line always travels forward.
          If one was living their life normally and suddenly decided to travel into the past, then their world line would loop back and continue into the path one is traveling in.
          The path one travels in never jumps or breaks, it is always continuous.
          Now, paradoxes are created when these world-lines intersect with each other at some point in time.
          There are plenty of paradoxes in travelling, but the most famous one is called the grandfather’s paradox.
          The idea is that if a guy named Nate were to travel into the past and kill his grandfather before he gave birth to his son, then his grandfather’s son would’ve never been born and Nate as well, which means that he would’ve never travelled into the past and killed his grandfather, implying that Nate would’ve been born.
          This ends up in a continuous loop which is like whether the chicken or egg was born first.
          Hence, travelling into the past is way more difficult than travelling into the future since it creates too many problems which mess with the linear flow of time.
        </p>

        <figure className="blog-figure">
          <img
            src="/assets/worldline.png"
            alt="World-line of the Earth revolving around the Sun"
            className="blog-image"
          />
          <figcaption className="figcaption">
            Figure 2: World-line of the Earth revolving around the Sun
          </figcaption>
        </figure>

        <figure className="blog-figure">
          <img
            src="/assets/grandfatherparadox.png"
            alt="Visual representation of the grandfather paradox"
            className="blog-image"
          />
          <figcaption className="figcaption">
            Figure 3: Visual representation of the grandfather paradox
          </figcaption>
        </figure>
      </section>

      <section className="blog-section">
        <p>
          According to the theory of self-consistency and parallel worlds, the grandfather’s paradox would never be an issue.
          Parallel worlds could be like Heisenberg's principle which states that the exact position and velocity of a particle (electron of an atom) could not be detected.
          According to Ronal C. Cosser, the more we know about the position, the less we know about the speed.
          This could lead to the existence of parallel worlds where the particle could’ve been at x location and in another world could’ve been at y position.
          For example, if a soccer player gets injured in a game, in one world he quits soccer and has a different future from the one in another world where he gets back up and wins a lot of trophies.
          In one world there could’ve been no World War 2, in another one Messi could’ve been the greatest player of all time.
          This is basically the idea.
          Now in the grandfather’s paradox killing the grandfather could’ve led to another world, where Nate is living his life after killing him, and in another one Nate does not exist.
          This is where self-consistency comes to play, which states that whatever happens is supposed to happen.
          This means that no matter how hard Nate tries to kill his grandfather and change the past he couldn’t.
          This does not object free will though.
          It's like knowing the sequences of a movie but unable to change it, despite having free will.
          This is because it’s just simply out of one's hand.
          Things are meant to go in flow.
          Another circumstance that can impact the occurrence of events is the butterfly effect.
          According to Edward Lorenz, the butterfly effect is the phenomenon in which the slightest change in a system can cause a greater impact in the outcome of future events.
          For example, the falling of an apple resulted in newton discovering the laws of gravity.
        </p>
      </section>

      <section className="blog-section">
        <p>
          Finally, what kind of technology would one need for time travel?
          Firstly, a wormhole is needed which opens at a point far away from the entrance point.
          One couldn’t enter a wormhole directly since its gravity is high enough to engulf a star.
          So, to avoid getting crushed by the wormhole, one would need a spacecraft that can manipulate gravity surrounding it while cruising.
          Gravity can be manipulated in multiple ways such as inverting it, if it is weak.
          If gravity is too strong, it can be diverted.
          Lastly, by taking account of the principles of quantum physics, if the spacecraft is made small enough, the journey could be survival.
          The smaller the object, the less the impact of gravity on it and if one could possibly shrink the size of the craft at the jump point of the wormhole, gravity shouldn't be able to crush it.
          The spacecraft should be able to predict the best possible trajectory it should follow to enter the desired coordinates (considering all four dimensions including the right time) and must be autopilot, since the trajectories would be changing at every split nano second which is beyond the processing time of the human brain.
          Given a time of a few hundred years, artificial intelligence would be improved to be so quick with its responses, that it would be able respond almost as fast as the speed of light.
        </p>
      </section>

      <section className="blog-section">
        <p>
          In conclusion, under crisis of overpopulation and deteriorating conditions of the Earth, time travel could create routes to the dinosaur age where humans could migrate and have a living and who knows if the homo-sapiens Neanderthals are us from the future.
        </p>
      </section>

      <section className="blog-section">
        <h2 className="blog-subheading">Works Cited</h2>
        <p>
          Al-Khalili, Jim. Black holes, wormholes and time machines. CRC Press, 2011, https://books.google.com/books?hl=en&lr=&id=v__ukMwx4IwC&oi=fnd&pg=PP1&dq=time+travel+through+wormholes&ots=sbd7XQnKn0&sig=EafgwSrRGZ-3CVbExd-S1ofj3YU#v=onepage&q=time%20travel%20through%20wormholes&f=false
        </p>
        <p>
          Cosser, Ronald C. Illustrating the problem described by Heisenberg's uncertainty principle. Journal of Chemical Education, 59.4, 300, 1982, https://pubs.acs.org/doi/pdf/10.1021/ed059p300
        </p>
        <p>
          Einstein, Albert. How I created the theory of relativity. Physics today 35.8, 45-47, 1982, https://courses.physics.ucsd.edu/2022/Fall/physics2d/einsteinonrelativity.pdf
        </p>
        <p>
          Lorenz, Edward. The butterfly effect. World Scientific Series on Nonlinear Science Series A 39, 91-94, 2000, https://books.google.com/books?hl=en&lr=&id=olJqDQAAQBAJ&oi=fnd&pg=PA91&dq=the+butterfly+effect&ots=_3FQkzW87k&sig=vS1YdLCgesicKl7AD95nOvZQkx0#v=onepage&q=the%20butterfly%20effect&f=false
        </p>
        <p>
          Nolan, Christopher. Interstellar. Paramount Pictures, 2014, https://www.youtube.com/watch?v=LApJx1sz-NA
        </p>
      </section>

      </article>
    </div>
  );
}
